// scheduling/components/reports/NicheReport.jsx
// Niche Involvement report (Rachel Stanley's request, 2026-09-15).
//
// Replaces Admin > Niche Interests, which listed ranked preferences only.
//
// --- THE ONE THING THIS REPORT HAS TO GET ACROSS ---------------------------
// "Niche involvement" has two different answers here and they must not read as one
// number: a **rank** is what the staffer SAID they want (self-selected, #1-#5, 90 of 303
// have set any), and **hours** are what they have actually charged (270 of 303). A blank
// rank is not disinterest, and hours without a rank are not a preference. The legend and
// the coverage line exist because Rachel surveyed a different population (Supervisors and
// up) and will otherwise read the gaps as answers.
//
// Hours are a ROLLING window (experience_lookback_years, currently 3), not career totals,
// so the window is stated on screen from the server's value rather than hardcoded.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../../../hooks/useUrlState';
import { fetchNicheReport } from '../../services/reportsApi';
import { useSort, SortableTh, PlainTh } from './SortableTh';
import { exportCsv } from '../../utils/csv';
import { getExpLevel, BRAND } from '../../utils/schedConstants';

// Every section starts COLLAPSED. The default landing is "All niches" (19 of them), so
// opening even a couple means the page loads mid-table with the legend pushed off
// screen -- and which two got opened is arbitrary. Collapsed-by-default makes the
// section headers the index: each already states "N ranked / M above threshold / top
// Xh", which is the summary you want before choosing where to look.
const OPEN_BY_DEFAULT = 0;

const HOURS_LABEL = { 0: 'Any', 10: '10h+', 40: '40h+', 100: '100h+' };

const ROW_SORTS = {
  hours: { get: r => r.hours, dir: 'desc' },
  rank: { get: r => r.rank, dir: 'asc' },
  name: { get: r => String(r.name).toLowerCase(), dir: 'asc' },
  level: { get: r => String(r.level || ''), dir: 'asc' },
  dept: { get: r => String(r.dept || ''), dir: 'asc' },
  office: { get: r => String(r.office || ''), dir: 'asc' },
};

const PERSON_SORTS = {
  name: { get: r => String(r.name).toLowerCase(), dir: 'asc' },
  level: { get: r => String(r.level || ''), dir: 'asc' },
  dept: { get: r => String(r.dept || ''), dir: 'asc' },
  office: { get: r => String(r.office || ''), dir: 'asc' },
  ranked: { get: r => r.rankedCount, dir: 'desc' },
  niches: { get: r => r.nicheCount, dir: 'desc' },
  hours: { get: r => r.totalHours, dir: 'desc' },
};

const fmtH = (h) => (Math.round((Number(h) || 0) * 10) / 10).toLocaleString();

/** Self-selected preference rank. Visually distinct from anything hours-derived. */
function RankChip({ rank }) {
  if (rank == null) return <span className="sched-rpt-muted-sm">&mdash;</span>;
  return (
    <span title={`This staffer ranked this niche #${rank} among their own preferences`}
      style={{
        display: 'inline-block', minWidth: 26, textAlign: 'center',
        fontSize: 11, fontWeight: 800, color: '#fff', background: BRAND.oceanBlue,
        borderRadius: 10, padding: '1px 7px',
      }}>#{rank}</span>
  );
}

/** Experience tier from percentile — the same scale the Experience tab uses. */
function TierChip({ percentile }) {
  if (percentile == null) return <span className="sched-rpt-muted-sm">&mdash;</span>;
  const lvl = getExpLevel(percentile);
  return (
    <span title={`${lvl.label} — top ${Math.max(1, Math.round(100 - percentile))}% of staff with meaningful hours in this niche`}
      style={{
        fontSize: 10, fontWeight: 700, color: '#fff', background: lvl.color,
        borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
      }}>{lvl.label}</span>
  );
}

export default function NicheReport() {
  const navigate = useNavigate();
  const [minHours, setMinHours] = useUrlState('nh', 40, {
    serialize: v => String(v),
    deserialize: s => (Number.isFinite(Number(s)) ? Number(s) : 40),
    validate: [0, 10, 40, 100],
  });
  const [view, setView] = useUrlState('nview', 'niche', { validate: ['niche', 'person'] });
  const [nicheFilter, setNicheFilter] = useUrlState('n', '');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({});          // niche -> bool override
  const rowSort = useSort(ROW_SORTS, 'hours', 'nsort');
  const personSort = useSort(PERSON_SORTS, 'name', 'psort');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchNicheReport({ minHours })
      .then(r => { if (!cancelled) { setData(r); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [minHours]);

  const q = search.trim().toLowerCase();
  const matches = (r) => !q || String(r.name).toLowerCase().includes(q);

  // ── By-niche grouping ────────────────────────────────────────────────────
  const sections = useMemo(() => {
    if (!data) return [];
    const byNiche = {};
    for (const r of data.rows) {
      if (!matches(r)) continue;
      (byNiche[r.niche] = byNiche[r.niche] || []).push(r);
    }
    const sumOf = {};
    for (const s of data.summaries) sumOf[s.niche] = s;
    // Most-ranked first — this report exists to support niches, and demand is the
    // thing you act on. Ties fall back to alphabetical for stability.
    return (data.summaries.map(s => s.niche))
      .filter(n => !nicheFilter || n === nicheFilter)
      .map(niche => ({ niche, rows: byNiche[niche] || [], summary: sumOf[niche] || {} }))
      .filter(s => s.rows.length > 0 || !q)
      .sort((a, b) => (b.summary.rankedCount || 0) - (a.summary.rankedCount || 0)
        || a.niche.localeCompare(b.niche));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, nicheFilter, q]);

  // ── By-person pivot ──────────────────────────────────────────────────────
  const people = useMemo(() => {
    if (!data) return [];
    const byStaff = {};
    for (const r of data.rows) {
      if (!matches(r)) continue;
      if (nicheFilter && r.niche !== nicheFilter) continue;
      const p = byStaff[r.staffId] || (byStaff[r.staffId] = {
        staffId: r.staffId, name: r.name, level: r.level, dept: r.dept, office: r.office,
        niches: [], rankedCount: 0, nicheCount: 0, totalHours: 0,
      });
      p.niches.push(r);
      p.nicheCount += 1;
      p.totalHours += r.hours;
      if (r.rank != null) p.rankedCount += 1;
    }
    const list = Object.values(byStaff);
    // Ranked preferences first, then biggest hours — the order you'd read a person in.
    for (const p of list) {
      p.niches.sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99) || b.hours - a.hours);
      p.totalHours = Math.round(p.totalHours * 10) / 10;
    }
    return personSort.sortRows(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, nicheFilter, q, personSort]);

  const allNiches = useMemo(() => (data?.summaries || []).map(s => s.niche).sort(), [data]);
  const isOpen = (niche, idx) => (open[niche] === undefined ? idx < OPEN_BY_DEFAULT : open[niche]);
  const lookback = data?.lookbackYears;

  // ── CSV — scoped to whatever is on screen ────────────────────────────────
  const NICHE_COLS = [
    { label: 'Niche', get: r => r.niche },
    { label: 'Staff', get: r => r.name },
    { label: 'Preference rank', get: r => (r.rank == null ? '' : `#${r.rank}`) },
    { label: `Hours (last ${lookback ?? '?'}y)`, get: r => r.hours },
    { label: 'Experience tier', get: r => (r.percentile == null ? '' : getExpLevel(r.percentile).label) },
    { label: 'Percentile', get: r => (r.percentile == null ? '' : r.percentile) },
    { label: 'Level', get: r => r.level },
    { label: 'Dept', get: r => r.dept },
    { label: 'Office', get: r => r.office },
  ];

  const exportView = () => {
    if (view === 'person') {
      exportCsv('rprs-niche-involvement-by-person', people.flatMap(p => p.niches), NICHE_COLS);
    } else {
      exportCsv('rprs-niche-involvement', sections.flatMap(s => rowSort.sortRows(s.rows)), NICHE_COLS);
    }
  };
  const exportNiche = (section) =>
    exportCsv(`rprs-niche-${section.niche.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`,
      rowSort.sortRows(section.rows), NICHE_COLS);

  return (
    <div className="sched-rpt-root">
      {/* ── The two-signals legend ──
          Above the controls on purpose: it is the thing that stops a rank and an hours
          figure being read as one number, so it has to be seen BEFORE the filters, not
          after the first table. Horizontal band rather than the stacked
          .sched-rpt-legend, which is built to sit beside a stat group this report does
          not have. */}
      {data && (
        <div className="sched-rpt-legend-band">
          <div className="sched-rpt-muted-sm">
            <RankChip rank={1} />{' '}
            <strong>Rank #1&ndash;#5 is what the staffer chose</strong> &mdash; their own ranked niche
            preferences, set on their profile. Not an assignment, not a measure of experience.
          </div>
          <div className="sched-rpt-muted-sm">
            <strong>Hours are what they have actually worked</strong> &mdash; charged time in Practice
            Engine over the last <strong>{lookback ?? '?'} year{lookback === 1 ? '' : 's'}</strong>{' '}
            (a rolling window, set by <em>experience_lookback_years</em> in Admin, not a career total).
            The tier is their percentile among staff with at least {data.minHoursThreshold ?? 10}h in
            that niche.
          </div>
          <div className="sched-rpt-muted-sm">
            ⚠ <strong>A blank rank is not disinterest.</strong> Setting niche preferences is optional and
            only some staff have done it, so the ranked list is a subset of who might be interested
            &mdash; it is not a firm-wide survey.
          </div>
          {data.lastCalculated && (
            <div className="sched-rpt-muted-sm">
              Hours last recalculated <strong>{new Date(data.lastCalculated).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</strong>{' '}
              (Central), on each PE sync. Rankings are live.
            </div>
          )}
        </div>
      )}

      {/* ── Controls ── */}
      <div className="sched-cap-toolbar">
        <span className="sched-cap-toolbar-label"
          title="Who appears in each niche. Anyone who RANKED a niche always appears, even with no hours — their stated preference is the point.">MIN HOURS</span>
        {[0, 10, 40, 100].map(h => (
          <button key={h}
            className={`sched-mgr-pill${minHours === h ? ' sched-mgr-pill--active' : ''}`}
            onClick={() => setMinHours(h)}
            title={h === 0
              ? 'Everyone with any charged hours in the niche — complete, but long'
              : `Only staff with at least ${h} charged hours in the niche`}
          >{HOURS_LABEL[h]}</button>
        ))}
        <span style={{ width: 12 }} />
        <span className="sched-cap-toolbar-label" title="Same data, two shapes.">VIEW</span>
        <button className={`sched-mgr-pill${view === 'niche' ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setView('niche')} title="Group by niche — who is involved in each one.">By niche</button>
        <button className={`sched-mgr-pill${view === 'person' ? ' sched-mgr-pill--active' : ''}`}
          onClick={() => setView('person')} title="Group by person — what each staffer is involved in. The spreadsheet shape.">By person</button>
        {data && (
          <button className="sched-rpt-csv" style={{ marginLeft: 'auto' }} onClick={exportView}
            title="Download exactly what is shown, including the min-hours, niche and name filters.">
            CSV (this view)
          </button>
        )}
      </div>

      <div className="sched-rpt-builder">
        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Limit to a single niche.">Niche</label>
            <select className="sched-rpt-bsel" value={nicheFilter} onChange={e => setNicheFilter(e.target.value)}>
              <option value="">All niches ({allNiches.length})</option>
              {allNiches.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
          <span className="sched-rpt-bfield">
            <label className="sched-rpt-blabel" title="Substring match on staff name.">Staff</label>
            <input className="sched-rpt-btext" placeholder="name contains…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </span>
          {(nicheFilter || search.trim() || minHours !== 40) && (
            <button className="sched-rpt-clear"
              onClick={() => { setNicheFilter(''); setSearch(''); setMinHours(40); }}>clear filters</button>
          )}
        </div>
      </div>

      {error && <div className="sched-rpt-error">Couldn&rsquo;t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading&hellip;</div>}

      {!loading && !error && data && (
        <>

          {/* ── By niche ── */}
          {view === 'niche' && sections.map((s, idx) => {
            const openNow = isOpen(s.niche, idx);
            const rows = rowSort.sortRows(s.rows);
            return (
              <div key={s.niche} className="sched-rpt-incoming">
                <button className="sched-rpt-disclose"
                  onClick={() => setOpen(o => ({ ...o, [s.niche]: !openNow }))}>
                  {openNow ? '▾' : '▸'} {s.niche}
                  <span className="sched-rpt-muted-sm">
                    {' '}&mdash; {s.summary.rankedCount || 0} ranked it
                    {' · '}{s.summary.atThresholdCount || 0} with {minHours === 0 ? 'any hours' : `${minHours}h+`}
                    {s.summary.maxHours ? ` · top ${fmtH(s.summary.maxHours)}h` : ''}
                    {rows.length !== s.rows.length || q ? ` · showing ${rows.length}` : ''}
                  </span>
                </button>
                {openNow && (
                  <>
                    <button className="sched-rpt-csv" style={{ marginBottom: 6 }}
                      onClick={() => exportNiche(s)}
                      title={`Download just ${s.niche} as CSV.`}>CSV ({s.niche})</button>
                    <div className="sched-mgr-table-wrap sched-rpt-scroll">
                      <table className="sched-mgr-table sched-rpt-table">
                        <thead>
                          <tr className="sched-rpt-colrow">
                            <SortableTh sort={rowSort} sortKey="rank" label="Pref"
                              title="The staffer's OWN ranked preference (#1 = first choice). Em-dash means they have not ranked this niche — which is not the same as not wanting it." />
                            <SortableTh sort={rowSort} sortKey="name" label="Staff"
                              title="Click a row to open their profile." />
                            <SortableTh sort={rowSort} sortKey="level" label="Level" title="PE staff level." />
                            <SortableTh sort={rowSort} sortKey="dept" label="Dept" title="PE department code." />
                            <SortableTh sort={rowSort} sortKey="office" label="Office" title="PE office code." />
                            <SortableTh sort={rowSort} sortKey="hours" label={`Hours (${lookback ?? '?'}y)`}
                              className="sched-mgr-th-right"
                              title={`Charged time in this niche over the last ${lookback ?? '?'} years. Zero means they ranked it but have not worked in it.`} />
                            <PlainTh label="Experience"
                              title="Percentile tier among staff with meaningful hours in this niche — the same scale the Experience tab uses." />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => (
                            <tr key={`${r.staffId}-${r.niche}`} style={{ cursor: 'pointer' }}
                              onClick={() => navigate(`/schedule/employee/${r.staffId}?tab=profile`)}>
                              <td><RankChip rank={r.rank} /></td>
                              <td style={{ color: BRAND.oceanBlue, fontWeight: 600 }}>{r.name}</td>
                              <td className="sched-rpt-muted-sm">{r.level}</td>
                              <td className="sched-rpt-muted-sm">{r.dept}</td>
                              <td className="sched-rpt-muted-sm">{r.office || '—'}</td>
                              <td className="sched-mgr-td-right sched-rpt-pct">{r.hours ? fmtH(r.hours) : '—'}</td>
                              <td><TierChip percentile={r.percentile} /></td>
                            </tr>
                          ))}
                          {rows.length === 0 && (
                            <tr><td colSpan={7} className="sched-rpt-muted">
                              {q ? 'No staff match the name filter in this niche.' : 'Nobody meets this threshold.'}
                            </td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* ── By person ── */}
          {view === 'person' && (
            <div className="sched-mgr-table-wrap sched-rpt-scroll">
              <table className="sched-mgr-table sched-rpt-table">
                <thead>
                  <tr className="sched-rpt-colrow">
                    <SortableTh sort={personSort} sortKey="name" label="Staff" title="Click a row to open their profile." />
                    <SortableTh sort={personSort} sortKey="level" label="Level" title="PE staff level." />
                    <SortableTh sort={personSort} sortKey="dept" label="Dept" title="PE department code." />
                    <SortableTh sort={personSort} sortKey="office" label="Office" title="PE office code." />
                    <SortableTh sort={personSort} sortKey="ranked" label="Ranked" className="sched-mgr-th-right"
                      title="How many niches this staffer has ranked as a preference (0–5)." />
                    <SortableTh sort={personSort} sortKey="hours" label={`Hours (${lookback ?? '?'}y)`}
                      className="sched-mgr-th-right"
                      title="Total charged hours across the niches shown, over the lookback window. Not their total hours — non-niche work is not counted here." />
                    <PlainTh label="Niches" title="Ranked preferences first (#n), then the rest by hours." />
                  </tr>
                </thead>
                <tbody>
                  {people.map(p => (
                    <tr key={p.staffId} style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/schedule/employee/${p.staffId}?tab=profile`)}>
                      <td style={{ color: BRAND.oceanBlue, fontWeight: 600 }}>{p.name}</td>
                      <td className="sched-rpt-muted-sm">{p.level}</td>
                      <td className="sched-rpt-muted-sm">{p.dept}</td>
                      <td className="sched-rpt-muted-sm">{p.office || '—'}</td>
                      <td className="sched-mgr-td-right sched-rpt-pct">{p.rankedCount || '—'}</td>
                      <td className="sched-mgr-td-right sched-rpt-pct">{fmtH(p.totalHours)}</td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {p.niches.map(n => (
                            <span key={n.niche} className="sched-rpt-chip"
                              title={`${n.niche} — ${n.rank != null ? `ranked #${n.rank}` : 'not ranked'}, ${fmtH(n.hours)}h in the last ${lookback ?? '?'} years`}
                              style={{
                                background: n.rank != null ? '#E0F2F1' : '#F3F4F6',
                                color: n.rank != null ? BRAND.deepTeal : '#6B7280',
                                fontWeight: n.rank != null ? 700 : 500,
                              }}>
                              {n.rank != null ? `#${n.rank} ` : ''}{n.niche}
                              {n.hours ? ` · ${fmtH(n.hours)}h` : ''}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {people.length === 0 && (
                    <tr><td colSpan={7} className="sched-rpt-muted">No staff match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="sched-rpt-footnote">
            {data.rows.length.toLocaleString()} person&times;niche rows at {HOURS_LABEL[minHours]}
            {' '}across {data.summaries.length} niches.
            {data.excludedNiches?.length > 0 && (
              <> <strong>{data.excludedNiches.join(', ')}</strong> {data.excludedNiches.length === 1 ? 'is' : 'are'}{' '}
                excluded &mdash; not a real niche, just the bucket for clients whose industry is unset.</>
            )}
          </div>
        </>
      )}
    </div>
  );
}
